import ExcelJS from 'exceljs';

interface IHeader {
  header: string;
  key: string;
  width: number;
  example?: string;
  hideNote?: boolean;
  reference?: string;
  required?: boolean;
  isDate?: boolean;
}

interface IReferenceData {
  [key: string]: string[];
}

export interface TemplateConfig {
  name: string;
  headers?: IHeader[];
  referenceData?: IReferenceData;
  data?: any[]; // Add optional data array
}

// Constants for better maintainability
const EXCEL_CONSTANTS = {
  MIME_TYPE: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  COLORS: {
    HEADER_BG: 'FFD3D3D3',
    NOTES_TEXT: 'FFFF0000',
    NOTES_BG: 'FFDEEBF7',
  },
  VALIDATION_ROWS: 1000,
  NOTES_ROW_HEIGHT: 45,
} as const;

// Helper function to create reference sheet efficiently
const createReferenceSheet = (
  workbook: ExcelJS.Workbook,
  referenceData: Record<string, string[]>
): Record<string, string> => {
  if (Object.keys(referenceData).length === 0) {
    return {};
  }

  const referenceSheet = workbook.addWorksheet('Referensi');
  const referenceColumnMap: Record<string, string> = {};

  // Use Object.entries for better performance and readability
  Object.entries(referenceData).forEach(([refKey, refData], index) => {
    const currentColumn = index + 1;
    const columnLetter = getColumnLetter(currentColumn);
    referenceColumnMap[refKey] = columnLetter;

    // Set header
    const headerCell = referenceSheet.getCell(1, currentColumn);
    headerCell.value = refKey;
    headerCell.font = { bold: true };

    const values = Array.isArray(refData) ? refData : [];

    values.forEach((value, idx) => {
      const row = idx + 2;
      referenceSheet.getCell(row, currentColumn).value = value;
    });
  });

  return referenceColumnMap;
};

// Helper function to create notes text
const createNotesText = (header: any): string => {
  if (header.hideNote) return '';

  const parts = ['Jangan dihapus'];
  if (header.example) parts.push(`\nContoh: ${header.example}`);
  parts.push(header.required ? '\n*Wajib' : '\n(opsional)');

  return parts.join('');
};

// Helper function to apply cell styling
const applyCellStyling = {
  header: (cell: ExcelJS.Cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: EXCEL_CONSTANTS.COLORS.HEADER_BG },
    };
  },
  notes: (cell: ExcelJS.Cell) => {
    cell.font = { color: { argb: EXCEL_CONSTANTS.COLORS.NOTES_TEXT }, italic: true };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: EXCEL_CONSTANTS.COLORS.NOTES_BG },
    };
    cell.alignment = { wrapText: true, shrinkToFit: true };
  },
};

// Helper function to apply data validation efficiently
const applyDataValidation = (
  templateSheet: ExcelJS.Worksheet,
  headers: any[],
  referenceColumnMap: Record<string, string>,
  referenceData: Record<string, string[]>,
  startRow: number
) => {
  const endRow = startRow + EXCEL_CONSTANTS.VALIDATION_ROWS;

  headers.forEach((header, colIndex) => {
    const { reference: refKey, isDate } = header;
    const validationColumn = colIndex + 1;

    // Pre-calculate validation formula if needed
    const formula =
      refKey && referenceData[refKey]?.length && referenceColumnMap[refKey]
        ? `Referensi!$${referenceColumnMap[refKey]}$2:$${referenceColumnMap[refKey]}$${referenceData[refKey].length + 1}`
        : null;
    // Apply data validation to individual cells (ExcelJS requirement)

    for (let i = startRow; i <= endRow; i++) {
      const cell = templateSheet.getCell(i, validationColumn);
      // Apply number format to the range
      if (isDate) {
        // For date columns, apply date format to each cell individually
        cell.numFmt = 'dd/mm/yyyy';
      } else {
        // For non-date columns, apply text format to the entire column
        cell.numFmt = '@';
      }
      if (formula) {
        cell.dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [formula],
          showErrorMessage: true,
          errorTitle: 'Input Salah!',
          error: `Pilih ${refKey} dari daftar yang tersedia.`,
        };
      }
    }
  });
};

export const generateImportTemplate = async (config: TemplateConfig): Promise<void> => {
  const { name, headers = [], referenceData = {}, data } = config;

  // Early validation
  if (!name || headers.length === 0) {
    throw new Error('Template name and headers are required');
  }

  const workbook = new ExcelJS.Workbook();
  const templateSheet = workbook.addWorksheet('Input Data');

  // Create reference sheet and get column mapping
  const referenceColumnMap = createReferenceSheet(workbook, referenceData);

  // Create and style header row
  const headerRow = templateSheet.addRow(headers.map(h => h.header));
  headerRow.eachCell(applyCellStyling.header);

  // Create and style notes row
  const notesRowData = headers.map(createNotesText);
  const notesRow = templateSheet.addRow(notesRowData);
  notesRow.eachCell(applyCellStyling.notes);
  notesRow.alignment = { wrapText: true, shrinkToFit: true };

  // Add data rows if provided (batch operation)
  if (data?.length) {
    const dataRows = data.map(rowData => headers.map(header => rowData[header.key] || ''));
    templateSheet.addRows(dataRows);
  }

  // Apply data validation
  const startRow = notesRow.number + 1;
  applyDataValidation(templateSheet, headers, referenceColumnMap, referenceData, startRow);

  // Set column widths efficiently
  templateSheet.columns = headers.map(h => ({ width: h.width }));

  // Generate and download file
  try {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: EXCEL_CONSTANTS.MIME_TYPE });
    triggerDownload(blob, `${name}.xlsx`);
  } catch (error) {
    console.error('Error generating Excel template:', error);
    throw new Error('Failed to generate Excel template');
  }
};

// Helper function to convert column number to Excel column letter
const getColumnLetter = (columnNumber: number): string => {
  let result = '';
  while (columnNumber > 0) {
    columnNumber--;
    result = String.fromCharCode(65 + (columnNumber % 26)) + result;
    columnNumber = Math.floor(columnNumber / 26);
  }
  return result;
};

export const triggerDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link?.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};
